/**
 * Client Portal Dashboard
 * Main dashboard for external client users (investors)
 */

import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../utils/apiClient';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import useAsyncData from '../../hooks/useAsyncData';
import { formatDate } from '../../utils/formatters';
import useCurrencyFormatter from '../../hooks/useCurrencyFormatter';
import ContractReferenceCell from '../../components/contracts/ContractReferenceCell';

const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: `${color}.light`,
            color: `${color}.dark`,
            mr: 2,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ minHeight: '1.25em', display: 'block' }}>
            {subtitle || '\u00A0'}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrencyFormatter();
  const { data: dashboardData, loading, error } = useAsyncData(
    async () => {
      const [summaryRes, contractsRes, paymentsRes] = await Promise.all([
        apiGet('/client-portal/summary'),
        apiGet('/client-portal/my-contracts'),
        apiGet('/client-portal/my-payments?timing=upcoming'),
      ]);
      return {
        summary: summaryRes.data || summaryRes,
        contracts: (contractsRes.data || contractsRes).slice(0, 5),
        upcomingPayments: (paymentsRes.data || paymentsRes).filter(p => p.timing === 'upcoming').slice(0, 5),
      };
    },
    { defaultValue: { summary: null, contracts: [], upcomingPayments: [] }, extractData: false }
  );

  const { summary, contracts, upcomingPayments } = dashboardData;

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        My Portfolio
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <StatCard
            title="Active Contracts"
            value={summary?.contracts?.active || 0}
            icon={<DescriptionIcon />}
            color="primary"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <StatCard
            title="Total Capital"
            value={formatCurrency(summary?.contracts?.totalCapital || 0)}
            icon={<AccountBalanceIcon />}
            color="success"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <StatCard
            title="Average Rate"
            value={`${(summary?.contracts?.avgRate || 0).toFixed(2)}%`}
            icon={<TrendingUpIcon />}
            color="info"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <StatCard
            title="Upcoming Payments"
            value={summary?.upcomingPayments?.count || 0}
            subtitle={formatCurrency(summary?.upcomingPayments?.totalAmount || 0)}
            icon={<CalendarTodayIcon />}
            color="warning"
          />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        {/* Recent Contracts */}
        <Grid
          size={{
            xs: 12,
            md: 7
          }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">My Contracts</Typography>
              <Button size="small" onClick={() => navigate('/client-portal/contracts')}>
                View All
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reference</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Capital</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell>Maturity</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contracts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No contracts found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contracts.map((contract) => (
                      <TableRow
                        key={contract.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/client-portal/contracts/${contract.id}`)}
                      >
                        <TableCell><ContractReferenceCell reference={contract.reference} userReference={contract.user_reference} /></TableCell>
                        <TableCell>{contract.product_type}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(contract.capital_amount, contract.currency_code)}
                        </TableCell>
                        <TableCell align="right">{contract.interest_rate}%</TableCell>
                        <TableCell>{formatDate(contract.maturity_date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={contract.status}
                            size="small"
                            color={contract.status === 'Active' ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Upcoming Payments */}
        <Grid
          size={{
            xs: 12,
            md: 5
          }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Upcoming Payments</Typography>
              <Button size="small" onClick={() => navigate('/client-portal/payments')}>
                View All
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Contract</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {upcomingPayments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No upcoming payments
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    upcomingPayments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{formatDate(payment.payment_date)}</TableCell>
                        <TableCell><ContractReferenceCell reference={payment.contract_reference} userReference={payment.user_reference} /></TableCell>
                        <TableCell align="right">
                          {formatCurrency(payment.payment_amount, payment.currency_code)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientDashboard;
